import {
  LockedSelectionBadge,
  SELECTION_BADGE_TOP_OFFSET,
} from '@/components/app/editor/selection-chrome/locked-selection-badge'
import { MIN_CHROME_SIZE_PX } from '@/components/app/editor/selection-chrome/selection-chrome-constants'
import { chromeGeometry } from '@/lib/shapes/selection-chrome-preview'
import { selectionChromeFrameOfShape } from '@/lib/shapes/shape-coordinates'

import { resolveSelectionChromeFrame } from '../helpers/selection-chrome-frame'
import type { SelectionChromeLayerProps } from '../types/selection-chrome-layer-props'

/**
 * Lock / freeze badges, rendered as HTML rather than inside the chrome's `<svg>`.
 *
 * `LockedSelectionBadge` is a real button with a tooltip and positions itself relative to a parent
 * sized to the frame (`top: <offset>, right: -12`), so it needs an HTML box of exactly that size.
 * Putting it in a `<foreignObject>` would work but has to fight `overflow` clipping to let the badge
 * sit outside the frame's right edge — a plain sibling div is simpler and keeps the badge's own
 * layout contract intact. Each badge rotates with the box it is anchored to.
 *
 * **A multi-selection gets one badge per member, not one for the group.** That is a deliberate
 * design decision, not an artefact of the old implementation: every locked or frozen object
 * advertises its own state, so it stays obvious which objects the state applies to. Single-select
 * anchors its one badge to the (floored) selection frame instead.
 */
export function SelectionChromeBadgeOverlay({
  shapeManagerRef,
  selected,
  multiSelectChrome,
  selectionState,
  scale,
}: SelectionChromeLayerProps) {
  const badge = selectionState?.badge ?? null

  if (!badge || selected.length === 0) return null

  const anchors =
    selected.length > 1
      ? // Per-member: each box is that member's own rotated visual box, with no minimum-size floor —
        // these are indicators, not hit targets, and the member outlines they sit on are unfloored.
        selected.map((shape) => ({
          key: shape.id,
          geometry: chromeGeometry(selectionChromeFrameOfShape(shape), {
            scale,
            devicePixelSnap: true,
          }),
        }))
      : (() => {
          const frame = resolveSelectionChromeFrame(selected, multiSelectChrome)

          return frame
            ? [
                {
                  key: 'selection',
                  geometry: chromeGeometry(frame, {
                    scale,
                    minScreenPx: MIN_CHROME_SIZE_PX,
                    devicePixelSnap: true,
                  }),
                },
              ]
            : []
        })()

  return (
    <>
      {anchors.map(({ key, geometry: { rect, pivot, rotationDeg } }) => (
        <div
          key={key}
          className="absolute"
          style={{
            left: rect.x,
            top: rect.y,
            width: rect.width,
            height: rect.height,
            transform: rotationDeg ? `rotate(${rotationDeg}deg)` : undefined,
            transformOrigin: `${pivot.x - rect.x}px ${pivot.y - rect.y}px`,
            pointerEvents: 'none',
          }}
        >
          {/* The offset scales, the icon does not: the badge stays a constant 24px at every zoom
              while its anchor tracks the same point on the object, as it did inside the scaled
              canvas. */}
          <LockedSelectionBadge
            variant={badge}
            offsetTopPx={SELECTION_BADGE_TOP_OFFSET * scale}
            onUnlockClick={() => shapeManagerRef?.current?.unlockSelectionFromChromeBadge?.()}
            onUnfreezeClick={() => shapeManagerRef?.current?.unfreezeSelectionFromChromeBadge?.()}
          />
        </div>
      ))}
    </>
  )
}
